/** Utilities for cleaning and enhancing AI-generated article HTML before rendering. */

const PLANNING_HEADING =
  /(seo\s*metadata|implementation\s*details|content\s*gap|internal\s*linking\s*plan|external\s*source\s*recommendation|suggested\s*ai\s*overview|suggested\s*featured\s*snippet|featured\s*snippet|image\s*alt\s*text|alt\s*text\s*suggestion|search\s*intent|primary\s*keyword|secondary\s*\/?\s*lsi|url\s*slug|meta\s*description|seo\s*title|anchor\s*text|structured\s*data|schema\s*markup)/i;

export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&nbsp;/g, " ")
    .replace(/&#39;|&rsquo;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

/** Removes SEO/planning blocks that should never appear in the published article. */
export function stripPlanningSections(html: string): string {
  let out = html.replace(
    /<(h[1-6])[^>]*>([\s\S]*?)<\/\1>([\s\S]*?)(?=<h[1-6][\s>]|$)/gi,
    (full, _tag, headingInner) =>
      PLANNING_HEADING.test(stripHtml(String(headingInner))) ? "" : full,
  );
  out = out.replace(
    /<(p|li)[^>]*>\s*<?[^>]*>?\s*(SEO Title|Meta Description|URL Slug|Primary Keyword|Secondary\s*\/?\s*LSI Keywords?|Search Intent|Link Relationship|Suggested Anchor Text)\s*:?[\s\S]*?<\/\1>/gi,
    "",
  );
  return out.replace(/(\s*<hr\s*\/?>\s*){2,}/gi, "<hr />").trim();
}

/** Strips scripts, iframes, inline handlers and full-document wrappers. */
export function sanitizeArticleHtml(html: string): string {
  return html
    .replace(/<!DOCTYPE[^>]*>/gi, "")
    .replace(/<\/?(html|head|body)[^>]*>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "");
}

export interface TocItem {
  id: string;
  text: string;
}

function slugifyHeading(text: string, index: number): string {
  const base = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return base ? `${base}-${index}` : `section-${index}`;
}

/** Removes the leading <h1> (rendered separately), adds ids to h2s and returns a TOC. */
export function prepareArticle(rawHtml: string): {
  html: string;
  toc: TocItem[];
  h1: string | null;
} {
  let html = addInternalLinks(dedupeSections(stripPlanningSections(sanitizeArticleHtml(rawHtml))));

  let h1: string | null = null;
  const h1Match = html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (h1Match) {
    h1 = stripHtml(h1Match[1]);
    html = html.replace(h1Match[0], "");
  }

  const toc: TocItem[] = [];
  let i = 0;
  html = html.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/gi, (_m, attrs, inner) => {
    i += 1;
    const text = stripHtml(String(inner));
    const id = slugifyHeading(text, i);
    toc.push({ id, text });
    return `<h2 id="${id}"${attrs}>${inner}</h2>`;
  });

  return { html: html.trim(), toc, h1 };
}

/** Contextual internal link targets, ordered most-specific first. */
const INTERNAL_LINK_MAP: { phrase: string; href: string }[] = [
  { phrase: "air conditioning installer Southern Highlands NSW", href: "/" },
  { phrase: "ACG Southern Highlands", href: "/" },
  { phrase: "ducted air conditioning", href: "/ac-types" },
  { phrase: "ducted system", href: "/ac-types" },
  { phrase: "split system", href: "/ac-types" },
  { phrase: "reverse cycle air conditioner", href: "/ac-types" },
  { phrase: "air conditioning installation", href: "/services" },
  { phrase: "air conditioning repairs", href: "/services" },
  { phrase: "servicing and maintenance", href: "/services" },
  { phrase: "Daikin", href: "/brands" },
  { phrase: "Mitsubishi Electric", href: "/brands" },
  { phrase: "ActronAir", href: "/brands" },
  { phrase: "Bowral", href: "/areas-we-service" },
  { phrase: "Mittagong", href: "/areas-we-service" },
  { phrase: "Moss Vale", href: "/areas-we-service" },
  { phrase: "interest free finance", href: "/finance-options" },
  { phrase: "finance options", href: "/finance-options" },
  { phrase: "maintenance plan", href: "/membership" },
  { phrase: "free quote", href: "/schedule-quote" },
  { phrase: "contact us", href: "/contact-us" },
];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Adds contextual internal links throughout the body copy (once per phrase). */
export function addInternalLinks(html: string): string {
  const parts = html.split(/(<a\b[\s\S]*?<\/a>|<h[1-6][\s\S]*?<\/h[1-6]>|<[^>]+>)/i);
  const used = new Set<string>();
  for (const { phrase, href } of INTERNAL_LINK_MAP) {
    if (new RegExp(`href=["']${escapeRe(href)}["']`, "i").test(html)) used.add(href);
  }
  const re = (p: string) => new RegExp(`\\b(${escapeRe(p)})\\b`, "i");
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    if (!seg || seg.startsWith("<")) continue;
    for (const { phrase, href } of INTERNAL_LINK_MAP) {
      if (used.has(href)) continue;
      const m = re(phrase);
      if (m.test(seg)) {
        parts[i] = seg.replace(m, `<a href="${href}">$1</a>`);
        used.add(href);
        break;
      }
    }
  }
  return parts.join("");
}

/** Removes duplicated auto-appended sections (e.g. two External Sources blocks). */
export function dedupeSections(html: string): string {
  const seen = new Set<string>();
  return html.replace(/<section class="further-reading"[\s\S]*?<\/section>/gi, (m) => {
    if (seen.has("fr")) return "";
    seen.add("fr");
    return m;
  });
}
